import { useEffect, useState } from 'react';
import 'date-fns';
import {
  getReportList,
  setExportCSV,
  getChargeBackReportList,
  setExportChargeBackCSV,
  getCouponReportList,
} from 'redux/actions/reports';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import Preloader from 'components/Preloader';
import FilterReport from './Filter';
import TableReport from './Table';
import { COUPON_REPORT } from './constants';
import { getDefaultSelectedProviders } from './reportProviders';
import { handleTypeName } from './utils';
import { exportCouponReportCsv } from './components/couponCsv';
import { EMPTY_REPORT_LIST, SET_REPORT_LOADING } from 'redux/actions/types';
import ErrorBoundaryWrapper from 'components/ErrorBoundaryWrapper';
import { allDays, columnTitles } from './configTable';
import useProviderConfig from 'hooks/useProviderConfig';
import { Paper } from '@mui/material';
import { useFilteredProviders } from 'components/Order/hooks/useFilteredProviders';
import { getConnectedProviders } from 'redux/actions/providers';
import usePermission from 'hooks/permission';
import * as all from 'constants/permissions';
// import { getBrandList } from 'redux/actions/business';

const tableHeadersHandler = () => {
  return columnTitles;
};

const Reports = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { groups, providers: providerConfig } = useProviderConfig();
  const { items } = useSelector(({ providers }) => providers);
  const { filteredConnectedProviders, filteredProviders } =
    useFilteredProviders();
  const { headerKeys } = useSelector(({ reports }) => reports);
  const checkPermission = usePermission();
  const isSuperAdmin = checkPermission(all['SUPER']);
  const CHARGEBACK_DISPUTE_REPORT = 'CHARGEBACK_DISPUTE_REPORT';
  const { brandIdsForReport, locationIdsForReport, isBrandLoading } =
    useSelector(({ business }) => business);
  localStorage.removeItem('redirectUrl');

  // Use different provider lists based on user role
  const providersToUse = isSuperAdmin
    ? filteredProviders
    : filteredConnectedProviders;

  const initialReportType = 'SUMMARY';

  const filterInitialValues = {
    type: initialReportType,
    start_date: new Date(),
    end_date: new Date(),
    days_of_week: allDays.map((item) => item.value),
    brandIds: brandIdsForReport || [],
    locationIds: locationIdsForReport || [],
    providers: getDefaultSelectedProviders(
      providersToUse || {},
      initialReportType
    ),
  };

  const { reportList, loading, filterList } = useSelector(
    ({ reports }) => reports
  );
  const [model, setModel] = useState({
    ...filterInitialValues,
  });

  const [currentFilter, setCurrentFilter] = useState(null);
  const [filterSubmitted, handleFilterSubmitted] = useState(false);
  const [lastReportFilter, handleLastReportFilter] =
    useState(filterInitialValues);
  const [tableTypeName, handleTableTypeName] = useState(
    filterInitialValues.type
  );
  const [saveTypeValue, handleSaveTypeValue] = useState(
    filterList.filter((item) => item.name)
  );
  const [page, setPage] = useState(0);
  const [loadingExport, setLoading] = useState(false);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [errorText, setErrorText] = useState(false);
  const [currentReport, setCurrentReport] = useState(filterInitialValues);

  useEffect(() => {
    setModel(filterInitialValues);
  }, [items, groups, providersToUse]);

  useEffect(() => {
    if (!isSuperAdmin) {
      dispatch(getConnectedProviders());
    }
    dispatch({ type: EMPTY_REPORT_LIST, payload: [] });
  }, [isSuperAdmin]);

  const handleFilterSubmit = (modelData) => {
    handleFilterSubmitted(true);
    setCurrentReport(modelData);
    handleLastReportFilter(modelData);
    setPage(0);
    dispatch({ type: SET_REPORT_LOADING, payload: true });
    if (modelData.type === CHARGEBACK_DISPUTE_REPORT) {
      dispatch(
        getChargeBackReportList({
          ...modelData,
          start_date: modelData.start_date,
          end_date: modelData.end_date,
          isReporting: true,
        })
      );
    } else if (modelData.type === COUPON_REPORT) {
      dispatch(
        getCouponReportList({
          ...modelData,
          start_date: modelData.start_date,
          end_date: modelData.end_date,
          isReporting: true,
        })
      );
    } else {
      dispatch(
        getReportList({
          ...modelData,
          start_date: modelData.start_date,
          end_date: modelData.end_date,
          isReporting: true,
        })
      );
    }
  };

  // Pagination
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // handleExport
  const handleExport = () => {
    if (currentReport.type === COUPON_REPORT) {
      exportCouponReportCsv(
        reportList,
        { t, providers: providerConfig },
        t(handleTypeName(COUPON_REPORT))
      );
      return;
    }
    setLoading(true);
    if (currentReport.type === CHARGEBACK_DISPUTE_REPORT) {
      dispatch(
        setExportChargeBackCSV({ ...lastReportFilter, isReporting: true })
      )
        .then(() => {
          setTimeout(() => {
            setLoading(false);
          }, 2000);
        })
        .catch(() => setLoading(false));
    } else {
      dispatch(setExportCSV({ ...lastReportFilter, isReporting: true }))
        .then(() => {
          setTimeout(() => {
            setLoading(false);
          }, 2000);
        })
        .catch(() => setLoading(false));
    }
  };
  return (
    <Paper sx={{ p: 3 }}>
      <ErrorBoundaryWrapper>
        {isBrandLoading && <Preloader overlay />}
        <FilterReport
          tableTypeName={tableTypeName}
          model={model}
          currentFilter={currentFilter}
          setCurrentFilter={setCurrentFilter}
          filterInitialValues={filterInitialValues}
          errorText={errorText}
          handleTableTypeName={handleTableTypeName}
          setModel={setModel}
          handleFilterSubmit={handleFilterSubmit}
          setErrorText={setErrorText}
          filteredList={providersToUse || {}}
        />
        <TableReport
          lastReportFilter={lastReportFilter}
          currentFilter={currentFilter}
          currentReport={currentReport}
          loadingExport={loadingExport}
          tableTypeName={tableTypeName}
          filterSubmitted={filterSubmitted}
          loading={loading}
          reportList={reportList}
          page={page}
          saveTypeValue={saveTypeValue}
          rowsPerPage={rowsPerPage}
          tableHeadersHandler={tableHeadersHandler}
          setPage={setPage}
          handleChangePage={handleChangePage}
          handleChangeRowsPerPage={handleChangeRowsPerPage}
          setRowsPerPage={setRowsPerPage}
          handleExport={handleExport}
          headerKeys={headerKeys}
        />
      </ErrorBoundaryWrapper>
      {loading && <Preloader overlay />}
    </Paper>
  );
};

export default Reports;
